import { Product } from '../../data/products'
import { 
  ShoppingBagIcon,
  CalendarIcon,
  CheckCircleIcon,
  FileTextIcon,
  DownloadIcon
} from '../Icons'

interface PurchasesScreenProps {
  purchases: PurchasedOrder[]
  version: 'v1' | 'v2' | 'v3' | 'v4'
}

export interface PurchasedOrder {
  id: string
  items: Product[]
  total: number
  purchasedAt: string
  status: 'completed'
}

const colors = {
  bg: '#050508',
  cardBg: '#141418',
  nestedBg: '#1c1c22',
  accent: '#9333ea',
  accentLight: '#a855f7',
  accentBg: 'rgba(147, 51, 234, 0.12)',
  accentBorder: 'rgba(147, 51, 234, 0.25)',
  cardBorder: 'rgba(255, 255, 255, 0.12)',
  text: '#ffffff',
  textMuted: 'rgba(255, 255, 255, 0.52)',
  green: '#22c55e',
}

export default function PurchasesScreen({ purchases, version }: PurchasesScreenProps) {
  if (version === 'v1') {
    return <PurchasesV1 purchases={purchases} />
  } else if (version === 'v2') {
    return <PurchasesV2 purchases={purchases} />
  } else {
    return <PurchasesV3 purchases={purchases} />
  }
}

// VERSION 1: Карточки с деталями
function PurchasesV1({ purchases }: { purchases: PurchasedOrder[] }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      paddingBottom: '80px',
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 16px',
        borderBottom: `1px solid ${colors.cardBorder}`,
        background: colors.cardBg,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '8px',
        }}>
          <ShoppingBagIcon size={28} color={colors.accentLight} />
          <h2 style={{
            fontSize: '24px',
            fontWeight: 700,
            color: colors.text,
            margin: 0,
          }}>
            Мои покупки
          </h2>
        </div>
        <p style={{
          fontSize: '14px',
          color: colors.textMuted,
          margin: 0,
        }}>
          {purchases.length} {purchases.length === 1 ? 'покупка' : 'покупок'}
        </p>
      </div>

      {/* Empty State */}
      {purchases.length === 0 && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 24px',
          textAlign: 'center',
        }}>
          <ShoppingBagIcon size={64} color={colors.textMuted} />
          <h3 style={{
            fontSize: '18px',
            fontWeight: 600,
            color: colors.text,
            marginTop: '16px',
            marginBottom: '8px',
          }}>
            Пока нет покупок
          </h3>
          <p style={{
            fontSize: '14px',
            color: colors.textMuted,
            margin: 0,
          }}>
            Ваши оплаченные заказы появятся здесь
          </p>
        </div>
      )}

      {/* Purchases List */}
      <div style={{ padding: '16px' }}>
        {purchases.map((purchase) => (
          <div
            key={purchase.id}
            style={{
              background: colors.cardBg,
              border: `1px solid ${colors.cardBorder}`,
              borderRadius: '16px',
              padding: '16px',
              marginBottom: '12px',
            }}
          >
            {/* Order Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <CheckCircleIcon size={20} color={colors.green} />
                <span style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: colors.text,
                }}>
                  {purchase.id}
                </span>
              </div>
              <span style={{
                fontSize: '18px',
                fontWeight: 700,
                color: colors.accentLight,
              }}>
                ₽{purchase.total.toLocaleString('ru-RU')}
              </span>
            </div>

            {/* Date */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginBottom: '12px',
            }}>
              <CalendarIcon size={16} color={colors.textMuted} />
              <span style={{
                fontSize: '13px',
                color: colors.textMuted,
              }}>
                {new Date(purchase.purchasedAt).toLocaleDateString('ru-RU', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>

            {/* Items */}
            <div style={{
              background: colors.nestedBg,
              borderRadius: '12px',
              padding: '12px',
              marginBottom: '12px',
            }}>
              {purchase.items.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: idx < purchase.items.length - 1 
                      ? `1px solid ${colors.cardBorder}` 
                      : 'none',
                  }}
                >
                  <div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: colors.text,
                      marginBottom: '2px',
                    }}>
                      {item.title}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: colors.textMuted,
                    }}>
                      {item.description}
                    </div>
                  </div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: colors.text,
                  }}>
                    {item.price}
                  </div>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div style={{
              display: 'flex',
              gap: '8px',
            }}>
              <button style={{
                flex: 1,
                padding: '10px',
                borderRadius: '10px',
                border: `1px solid ${colors.accentBorder}`,
                background: colors.accentBg,
                color: colors.accentLight,
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}>
                <FileTextIcon size={16} />
                Чек
              </button>
              <button style={{
                flex: 1,
                padding: '10px',
                borderRadius: '10px',
                border: `1px solid ${colors.cardBorder}`,
                background: colors.nestedBg,
                color: colors.text,
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}>
                <DownloadIcon size={16} />
                Скачать
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// VERSION 2: Компактный список
function PurchasesV2({ purchases }: { purchases: PurchasedOrder[] }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      paddingBottom: '80px',
    }}>
      {/* Header with Stats */}
      <div style={{
        padding: '20px 16px',
        background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`,
      }}>
        <h2 style={{
          fontSize: '28px',
          fontWeight: 700,
          color: '#fff',
          margin: 0,
          marginBottom: '12px',
        }}>
          Мои покупки
        </h2>
        <div style={{
          display: 'flex',
          gap: '16px',
        }}>
          <div style={{
            flex: 1,
            background: 'rgba(255, 255, 255, 0.15)',
            borderRadius: '12px',
            padding: '12px',
            backdropFilter: 'blur(10px)',
          }}>
            <div style={{
              fontSize: '24px',
              fontWeight: 700,
              color: '#fff',
              marginBottom: '4px',
            }}>
              {purchases.length}
            </div>
            <div style={{
              fontSize: '12px',
              color: 'rgba(255, 255, 255, 0.8)',
            }}>
              Покупок
            </div>
          </div>
          <div style={{
            flex: 1,
            background: 'rgba(255, 255, 255, 0.15)',
            borderRadius: '12px',
            padding: '12px',
            backdropFilter: 'blur(10px)',
          }}>
            <div style={{
              fontSize: '24px',
              fontWeight: 700,
              color: '#fff',
              marginBottom: '4px',
            }}>
              ₽{purchases.reduce((sum, p) => sum + p.total, 0).toLocaleString('ru-RU')}
            </div>
            <div style={{
              fontSize: '12px',
              color: 'rgba(255, 255, 255, 0.8)',
            }}>
              Всего
            </div>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {purchases.length === 0 && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 24px',
          textAlign: 'center',
        }}>
          <ShoppingBagIcon size={64} color={colors.textMuted} />
          <h3 style={{
            fontSize: '18px',
            fontWeight: 600,
            color: colors.text,
            marginTop: '16px',
            marginBottom: '8px',
          }}>
            Нет покупок
          </h3>
          <p style={{
            fontSize: '14px',
            color: colors.textMuted,
            margin: 0,
          }}>
            Оформите первый заказ!
          </p>
        </div>
      )}

      {/* Compact List */}
      <div style={{ padding: '16px' }}>
        {purchases.map((purchase) => (
          <div
            key={purchase.id}
            style={{
              background: colors.cardBg,
              border: `1px solid ${colors.cardBorder}`,
              borderRadius: '12px',
              padding: '14px',
              marginBottom: '10px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '6px',
              }}>
                <CheckCircleIcon size={18} color={colors.green} />
                <span style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: colors.text,
                }}>
                  {purchase.id}
                </span>
              </div>
              <div style={{
                fontSize: '12px',
                color: colors.textMuted,
                marginBottom: '4px',
              }}>
                {purchase.items.length} {purchase.items.length === 1 ? 'товар' : 'товара'}
              </div>
              <div style={{
                fontSize: '11px',
                color: colors.textMuted,
              }}>
                {new Date(purchase.purchasedAt).toLocaleDateString('ru-RU')}
              </div>
            </div>
            <div style={{
              textAlign: 'right',
            }}>
              <div style={{
                fontSize: '20px',
                fontWeight: 700,
                color: colors.accentLight,
                marginBottom: '4px',
              }}>
                ₽{purchase.total.toLocaleString('ru-RU')}
              </div>
              <button style={{
                padding: '6px 12px',
                borderRadius: '8px',
                border: 'none',
                background: colors.accentBg,
                color: colors.accentLight,
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
              }}>
                Детали
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// VERSION 3: Табличный вид
function PurchasesV3({ purchases }: { purchases: PurchasedOrder[] }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      paddingBottom: '80px',
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 16px',
        borderBottom: `1px solid ${colors.cardBorder}`,
      }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: 700,
          color: colors.text,
          margin: 0,
          marginBottom: '4px',
        }}>
          История покупок
        </h2>
        <p style={{
          fontSize: '14px',
          color: colors.textMuted,
          margin: 0,
        }}>
          Всего покупок: {purchases.length}
        </p>
      </div>

      {/* Empty State */}
      {purchases.length === 0 && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 24px',
          textAlign: 'center',
        }}>
          <ShoppingBagIcon size={64} color={colors.textMuted} />
          <h3 style={{
            fontSize: '18px',
            fontWeight: 600,
            color: colors.text,
            marginTop: '16px',
            marginBottom: '8px',
          }}>
            История пуста
          </h3>
          <p style={{
            fontSize: '14px',
            color: colors.textMuted,
            margin: 0,
          }}>
            Здесь будут ваши покупки
          </p>
        </div>
      )}

      {/* Table View */}
      <div style={{ padding: '16px' }}>
        <div style={{
          background: colors.cardBg,
          border: `1px solid ${colors.cardBorder}`,
          borderRadius: '16px',
          overflow: 'hidden',
        }}>
          {purchases.map((purchase, idx) => (
            <div
              key={purchase.id}
              style={{
                padding: '16px',
                borderBottom: idx < purchases.length - 1 
                  ? `1px solid ${colors.cardBorder}` 
                  : 'none',
              }}
            >
              {/* Row Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: colors.green,
                  }} />
                  <span style={{
                    fontSize: '15px',
                    fontWeight: 600,
                    color: colors.text,
                  }}>
                    {purchase.id}
                  </span>
                </div>
                <span style={{
                  fontSize: '11px',
                  color: colors.textMuted,
                }}>
                  {new Date(purchase.purchasedAt).toLocaleDateString('ru-RU', {
                    day: 'numeric',
                    month: 'short',
                  })}
                </span>
              </div>

              {/* Items Summary */}
              <div style={{
                fontSize: '13px',
                color: colors.textMuted,
                marginBottom: '10px',
              }}>
                {purchase.items.map(i => i.title).join(', ')}
              </div>

              {/* Footer */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <span style={{
                  fontSize: '12px',
                  color: colors.textMuted,
                }}>
                  {purchase.items.length} {purchase.items.length === 1 ? 'позиция' : 'позиций'}
                </span>
                <span style={{
                  fontSize: '18px',
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  ₽{purchase.total.toLocaleString('ru-RU')}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
